<template>
  <!-- C5 A (Arnold, 08.10.): while a planner page is on its way, a skeleton of that page covers
       the old one — below the headline row, so the header, the section tabs and the second
       navigation column stay in place. Mounted once in Layouts/Default.vue; it shows only for
       visits to another planner page and only after 150 ms (composables/plannerSkeleton.js).
       The pulse stops with prefers-reduced-motion. -->
  <div
    v-if="skeleton !== null"
    class="absolute inset-x-0 bottom-0 z-10 flex flex-col gap-4 overflow-hidden bg-(--ds-surface-default)/80 px-4 pt-2 backdrop-blur-md md:px-8 ds:bg-(--ds-surface-app) ds:backdrop-blur-none"
    :style="frame"
    role="status"
    aria-busy="true"
    :data-skeleton="skeleton"
    data-test="planner-skeleton"
  >
    <span class="sr-only" v-text="$t('common.loading')" />

    <div
      v-if="skeleton === 'kpi-table'"
      class="grid grid-cols-2 gap-3 lg:grid-cols-4"
      aria-hidden="true"
      data-test="skeleton-kpis"
    >
      <div
        v-for="tile in 4"
        :key="tile"
        class="flex h-24 flex-col justify-center gap-3 rounded-(--ds-radius-card) border border-(--ds-border-app) bg-(--ds-surface-default) px-4"
      >
        <span :class="[BAR, 'h-3 w-1/2']" />
        <span :class="[BAR, 'h-6 w-3/4']" />
      </div>
    </div>

    <div
      v-if="skeleton === 'board'"
      class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
      aria-hidden="true"
      data-test="skeleton-board"
    >
      <div
        v-for="column in 4"
        :key="column"
        class="flex flex-col gap-3 rounded-(--ds-radius-card) border border-(--ds-border-app) bg-(--ds-surface-default) p-3"
      >
        <span :class="[BAR, 'h-4 w-1/3']" />
        <div
          v-for="card in column % 2 === 0 ? 2 : 3"
          :key="card"
          class="flex flex-col gap-2 rounded-(--ds-radius-md) border border-(--ds-border-app) p-3"
        >
          <span :class="[BAR, 'h-3 w-5/6']" />
          <span :class="[BAR, 'h-3 w-1/2']" />
        </div>
      </div>
    </div>

    <div
      v-else
      class="flex flex-col rounded-(--ds-radius-card) border border-(--ds-border-app) bg-(--ds-surface-default)"
      aria-hidden="true"
      data-test="skeleton-table"
    >
      <div
        class="flex items-center gap-3 border-b border-(--ds-border-app) px-4 py-3"
      >
        <span :class="[BAR, 'h-4 w-32']" />
        <span :class="[BAR, 'ml-auto h-9 w-40']" />
        <span :class="[BAR, 'h-9 w-28']" />
      </div>
      <div
        v-for="line in skeleton === 'kpi-table' ? 5 : 8"
        :key="line"
        class="flex items-center gap-4 border-b border-(--ds-border-app) px-4 py-3 last:border-b-0"
      >
        <span :class="[BAR, 'h-3 flex-[3]']" />
        <span :class="[BAR, 'h-3 flex-[2]']" />
        <span :class="[BAR, 'h-3 flex-1']" />
        <span :class="[BAR, 'h-3 flex-1']" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useDesignSystem } from '@/composables/designSystem'
import { usePlannerSkeleton } from '@/composables/plannerSkeleton'

const BAR =
  'block rounded-(--ds-radius-md) bg-(--ds-surface-subtle) motion-safe:animate-pulse'

const skeleton = usePlannerSkeleton()
const designSystem = useDesignSystem()

/**
 * Where the old page's content starts: below the section headline (SectionTabs, its height is
 * --event-app-tabs-offset in theme.css) or below the page headline (PageHeader). In the old look
 * the pages bring their own title (.page-head of PlannerPage): the skeleton starts below it and
 * takes the page's side gutters, so its tiles line up with the page's (QA-11). There it is a
 * frosted layer of the page's white over the page background image instead of a flat tint.
 */
const top = ref('0px')
const gutters = ref(null)

const frame = computed(() => ({
  top: top.value,
  ...(gutters.value === null
    ? {}
    : {
        paddingLeft: `${gutters.value.left}px`,
        paddingRight: `${gutters.value.right}px`,
      }),
}))

/** The old look: below the page title, with the gutters of its .page-body. */
function belowPageTitle(main) {
  const head = main.querySelector('.page-head')

  if (head === null) {
    return false
  }

  const area = main.getBoundingClientRect()
  const box = head.getBoundingClientRect()
  const margin = parseFloat(getComputedStyle(head).marginBottom) || 0

  top.value = `${Math.round(box.bottom - area.top + margin)}px`
  gutters.value = {
    left: Math.round(box.left - area.left),
    right: Math.round(area.right - box.right),
  }

  return true
}

watch(skeleton, (type) => {
  if (type === null) {
    return
  }

  const main = document.getElementById('main-content')
  const header = main?.querySelector(':scope > [data-test="page-header"]')
  gutters.value = null

  if (main?.hasAttribute('data-event-app-tabs')) {
    top.value = 'var(--event-app-tabs-offset, 0px)'
  } else if (header) {
    top.value = `${header.offsetTop + header.offsetHeight}px`
  } else if (designSystem.value || main === null || !belowPageTitle(main)) {
    top.value = '0px'
  }
})
</script>
